import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons by type
const createCustomIcon = (type) => {
  const colors = {
    facility: '#0F766E',
    specialist: '#2563EB',
    pharmacy: '#E11D48',
  };
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${colors[type] || colors.facility};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to update map bounds based on markers
const MapBoundsUpdater = ({ listings }) => {
  const map = useMap();
  
  useEffect(() => {
    if (listings.length > 0) {
      const validListings = listings.filter(l => l.lat && l.lng);
      if (validListings.length > 0) {
        const bounds = L.latLngBounds(validListings.map(l => [l.lat, l.lng]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [listings, map]);
  
  return null;
};

const ListingMap = ({ 
  listings = [], 
  center = [39.8283, -98.5795], // US center
  zoom = 4,
  className = '',
  onMarkerClick,
  selectedListing,
}) => {
  const mapRef = useRef(null);

  // Filter listings with valid coordinates
  const validListings = listings.filter(l => l.lat && l.lng);

  if (validListings.length === 0) {
    return (
      <div className={`${className} bg-gray-100 rounded-xl flex items-center justify-center`} style={{ height: '400px' }}>
        <p className="text-gray-500">No locations to display on map</p>
      </div>
    );
  }

  return (
    <div className={`${className} rounded-xl overflow-hidden border border-gray-200`} style={{ height: '400px' }}>
      <MapContainer
        ref={mapRef}
        center={validListings.length === 1 ? [validListings[0].lat, validListings[0].lng] : center}
        zoom={validListings.length === 1 ? 13 : zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsUpdater listings={validListings} />
        
        {validListings.map((listing) => (
          <Marker
            key={listing.listing_id}
            position={[listing.lat, listing.lng]}
            icon={createCustomIcon(listing.listing_type)}
            eventHandlers={{
              click: () => onMarkerClick && onMarkerClick(listing),
            }}
          >
            <Popup>
              <div className="p-2 min-w-[200px]">
                <h3 className="font-semibold text-gray-900 mb-1">{listing.name}</h3>
                <p className="text-sm text-gray-600 mb-2">
                  {listing.city}, {listing.state}
                </p>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-amber-500">★</span>
                  <span>{listing.rating_avg?.toFixed(1) || '0.0'}</span>
                  <span className="text-gray-400">({listing.review_count || 0})</span>
                </div>
                {listing.phone && (
                  <a
                    href={`tel:${listing.phone}`}
                    className="block mt-2 text-sm text-teal-700 hover:underline"
                  >
                    {listing.phone}
                  </a>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default ListingMap;
